"use client";

import { Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const inclusions = ["Comprehensive OTA performance audit", "Customized revenue growth roadmap", "Hospitality tech stack optimization", "Direct booking conversion strategies", "Ongoing revenue management support"];
function InquiryForm() {
  const {
    control,
    handleSubmit,
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    resetForm
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-blue-200 bg-blue-50">
        <p className="font-default text-2xl font-bold uppercase text-blue-900 mb-4">
          Inquiry Received
        </p>
        <p className="font-default text-base text-slate-700 mb-6">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="border-blue-900 text-blue-900 hover:bg-blue-100 rounded-lg uppercase font-default font-bold tracking-wider">
          Submit Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-slate-600">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Sunil Singh" {...field} className="bg-transparent border-0 border-b border-slate-300 px-0 h-11 font-default text-base placeholder:text-slate-400 focus-visible:border-blue-600 focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-slate-600">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="contact@hotel.com" {...field} className="bg-transparent border-0 border-b border-slate-300 px-0 h-11 font-default text-base placeholder:text-slate-400 focus-visible:border-blue-600 focus-visible:ring-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-xs uppercase tracking-widest text-slate-600">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+917972195929" {...field} className="bg-transparent border-0 border-b border-slate-300 px-0 h-11 font-default text-base placeholder:text-slate-400 focus-visible:border-blue-600 focus-visible:ring-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="hotel_name" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-slate-600">
              Hotel Property Name
            </FormLabel>
            <FormControl>
              <Input placeholder="Grand Luxury Resort" {...field} className="bg-transparent border-0 border-b border-slate-300 px-0 h-11 font-default text-base placeholder:text-slate-400 focus-visible:border-blue-600 focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_interest" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-slate-600">
              Service Interest
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="bg-transparent border-0 border-b border-slate-300 px-0 h-11 font-default text-base text-slate-900 focus-visible:border-blue-600 focus-visible:ring-0 w-full">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-white border border-slate-200 rounded-lg">
                  <SelectItem value="OTA Management">OTA Revenue Management</SelectItem>
                  <SelectItem value="Tech Implementation">Hospitality Tech Implementation</SelectItem>
                  <SelectItem value="Revenue Audit">Revenue Audit</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-slate-600">
              Project Details
            </FormLabel>
            <FormControl>
              <Textarea rows={4} placeholder="Tell us about your current revenue challenges..." {...field} className="bg-transparent border-0 border-b border-slate-300 px-0 py-2 font-default text-base placeholder:text-slate-400 focus-visible:border-blue-600 focus-visible:ring-0 resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-red-200 bg-red-50">
          <p className="font-default text-xs uppercase tracking-widest text-red-700">
            {submitError}
          </p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-14 bg-blue-900 text-white hover:bg-blue-800 rounded-lg font-default font-bold uppercase tracking-widest text-base shadow-lg">
        {isSubmitting ? "Submitting..." : "Request Consultation →"}
      </Button>

      <p className="font-default text-[10px] uppercase tracking-widest text-slate-400 text-center">
        We respond to all inquiries within 24 hours
      </p>
    </Form>;
}
export default function RevenueJourneyContact() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="2795" id="revenuejourneycontact" className="bg-slate-50 text-slate-900 py-24 md:py-32 lg:py-[180px] px-6 md:px-12">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-700 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-default text-xs uppercase tracking-[0.3em] text-blue-600 mb-8">
              [ Partner With Us ]
            </p>

            <h2 className="font-default font-bold uppercase text-5xl md:text-6xl lg:text-[4.5rem] leading-[0.9] tracking-tight mb-8">
              Maximize Your
              <br />
              <span className="text-blue-900">Hotel Revenue</span>
              <br />
              Today
            </h2>

            <p className="font-default text-lg text-slate-700 leading-relaxed mb-10 max-w-md">
              Ready to transform your property's performance? Sunil Singh and the HotelRevBaBa team provide data-driven strategies to optimize your OTA presence and boost direct bookings.
            </p>

            <ul className="space-y-4 mb-10">
              {inclusions.map((item, i) => <li data-index={i} key={i} className="flex items-start gap-4 font-default text-base text-slate-800">
                  <span className="flex-shrink-0 mt-1 w-5 h-5 border border-blue-600 flex items-center justify-center">
                    <Check className="w-3 h-3 text-blue-600" strokeWidth={3} />
                  </span>
                  <span>{item}</span>
                </li>)}
            </ul>

            <div className="inline-block px-4 py-3 border border-slate-200 bg-white">
              <p className="font-default text-xs uppercase tracking-widest text-slate-500">
                Initial Consultation:{" "}
                <span className="text-blue-900 font-bold">Complimentary</span>
              </p>
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:col-start-6 transition-all duration-700 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="bg-white border border-slate-100 p-8 md:p-12 shadow-lg rounded-lg">
              <div className="mb-10">
                <p className="font-default text-xs uppercase tracking-[0.3em] text-blue-600 mb-3">
                  / Get Started
                </p>
                <h3 className="font-default font-bold uppercase text-2xl md:text-3xl tracking-tight">
                  Schedule Your
                  <br />
                  Revenue Audit
                </h3>
              </div>

              <FormProvider formKey="revenue_journey_form_contact" formId="revenue_journey_form_contact" sectionName="revenuejourneycontact" defaultValues={{
              name: "",
              email: "",
              phone: "",
              hotel_name: "",
              service_interest: "",
              message: ""
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              hotel_name: {
                required: true
              },
              service_interest: {
                required: true
              }
            }} submitText="Request Consultation" successMessage="Thank you for your interest. Sunil Singh will review your details and contact you within 24 hours." errorMessage="Submission failed. Please check your details and try again.">
                <InquiryForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}